/**
 * Instanced pools over code-generated primitives: one draw call each however many effects are
 * alive. Dead instances collapse to zero scale. No asset needs a license row (DECISIONS.md).
 */
import {
  BoxGeometry,
  InstancedMesh,
  Matrix4,
  Object3D,
  Quaternion,
  SphereGeometry,
  Vector3,
  type BufferGeometry,
  type Scene,
} from 'three/webgpu';

import { createVfxMaterial, type VfxMaterial } from './material.js';

const _dummy = new Object3D();
const _zero = new Matrix4().makeScale(0, 0, 0);
const _dir = new Vector3();
const _up = new Vector3(0, 0, 1);
const _q = new Quaternion();

interface Slot {
  alive: boolean;
  born: number;
  life: number;
  position: Vector3;
  velocity: Vector3;
  quaternion: Quaternion;
  scale: Vector3;
  /** Pool-specific: growth for flashes, gravity for sparks. */
  grow: number;
  id: number;
}

function newSlot(): Slot {
  return {
    alive: false,
    born: 0,
    life: 1,
    position: new Vector3(),
    velocity: new Vector3(),
    quaternion: new Quaternion(),
    scale: new Vector3(1, 1, 1),
    grow: 0,
    id: -1,
  };
}

export type PoolKind = 'tracer' | 'projectile';

export interface PoolStats {
  capacity: number;
  alive: number;
}

/** A pool of instances with lifetimes; subclasses decide how a slot animates. */
export class InstancedPool {
  readonly mesh: InstancedMesh;
  readonly slots: Slot[];
  readonly vfx: VfxMaterial;
  private cursor = 0;

  constructor(
    readonly kind: PoolKind,
    geometry: BufferGeometry,
    hex: number,
    readonly capacity: number,
    intensity = 1,
  ) {
    this.vfx = createVfxMaterial(hex, intensity);
    this.mesh = new InstancedMesh(geometry, this.vfx.material, capacity);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.slots = Array.from({ length: capacity }, newSlot);
    for (let i = 0; i < capacity; i++) this.mesh.setMatrixAt(i, _zero);
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  addTo(scene: Scene): void {
    scene.add(this.mesh);
  }

  /** A free slot, or the oldest live one when the pool is full. */
  protected take(now: number, life: number): Slot {
    let slot: Slot | undefined;
    for (let i = 0; i < this.capacity; i++) {
      const s = this.slots[(this.cursor + i) % this.capacity]!;
      if (!s.alive) {
        slot = s;
        this.cursor = (this.cursor + i + 1) % this.capacity;
        break;
      }
    }
    if (!slot) {
      slot = this.slots.reduce((a, b) => (a.born <= b.born ? a : b));
    }
    slot.alive = true;
    slot.born = now;
    slot.life = life;
    slot.velocity.set(0, 0, 0);
    slot.quaternion.identity();
    slot.scale.set(1, 1, 1);
    slot.grow = 0;
    slot.id = -1;
    return slot;
  }

  stats(): PoolStats {
    return { capacity: this.capacity, alive: this.slots.filter((s) => s.alive).length };
  }

  /** Writes instance matrices; `now` in seconds. */
  update(now: number, dt: number): void {
    for (let i = 0; i < this.capacity; i++) {
      const s = this.slots[i]!;
      if (!s.alive) continue;
      const age = now - s.born;
      if (age >= s.life) {
        s.alive = false;
        this.mesh.setMatrixAt(i, _zero);
        continue;
      }
      this.animate(s, age / s.life, dt);
      _dummy.position.copy(s.position);
      _dummy.quaternion.copy(s.quaternion);
      _dummy.scale.copy(s.scale);
      _dummy.updateMatrix();
      this.mesh.setMatrixAt(i, _dummy.matrix);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /** Per-frame evolution of a live slot at normalised age `t` (0..1). */
  protected animate(_s: Slot, _t: number, _dt: number): void {}

  clear(): void {
    for (let i = 0; i < this.capacity; i++) {
      this.slots[i]!.alive = false;
      this.mesh.setMatrixAt(i, _zero);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.vfx.material.dispose();
    this.mesh.dispose();
  }
}

/** Aligns a slot's +Z with the segment a→b and stretches it to its length. */
function orientSegment(
  s: Slot,
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  width: number,
): void {
  s.position.set((ax + bx) / 2, (ay + by) / 2, (az + bz) / 2);
  _dir.set(bx - ax, by - ay, bz - az);
  const len = _dir.length();
  if (len > 1e-6) {
    _dir.divideScalar(len);
    _q.setFromUnitVectors(_up, _dir);
    s.quaternion.copy(_q);
  }
  s.scale.set(width, width, Math.max(len, 0.01));
}

export class TracerPool extends InstancedPool {
  constructor(capacity = 48) {
    super('tracer', new BoxGeometry(1, 1, 1), 0xffd27a, capacity, 4);
  }

  spawn(now: number, from: readonly number[], to: readonly number[]): void {
    const s = this.take(now, 0.12);
    orientSegment(
      s,
      from[0] ?? 0,
      from[1] ?? 0,
      from[2] ?? 0,
      to[0] ?? 0,
      to[1] ?? 0,
      to[2] ?? 0,
      0.07,
    );
    s.grow = s.scale.x;
  }

  protected override animate(s: Slot, t: number): void {
    const w = s.grow * (1 - t);
    s.scale.x = w;
    s.scale.y = w;
  }
}

export interface ProjectileVisual {
  id: number;
  x: number;
  y: number;
  z: number;
  radius: number;
}

/** Projectiles are not fire-and-forget: they are synced from the state every frame. */
export class ProjectilePool extends InstancedPool {
  constructor(capacity = 32) {
    super('projectile', new SphereGeometry(1, 10, 8), 0xffe0a0, capacity, 3);
  }

  sync(list: readonly ProjectileVisual[]): void {
    const n = Math.min(list.length, this.capacity);
    for (let i = 0; i < this.capacity; i++) {
      const s = this.slots[i]!;
      const p = list[i];
      if (i < n && p) {
        s.alive = true;
        s.position.set(p.x, p.y, p.z);
        const r = Math.max(0.18, p.radius * 0.8);
        _dummy.position.copy(s.position);
        _dummy.quaternion.identity();
        _dummy.scale.set(r, r, r);
        _dummy.updateMatrix();
        this.mesh.setMatrixAt(i, _dummy.matrix);
      } else if (s.alive) {
        s.alive = false;
        this.mesh.setMatrixAt(i, _zero);
      }
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  override update(): void {
    // Synced explicitly; nothing ages.
  }
}
